import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton.tsx';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider.jsx';
import { FollowSearch } from '../pdpMessages.tsx';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton.tsx';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';

const PdpSharedRegistrationPromo = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRegistrationPromo" */ '../shared/PdpSharedRegistrationPromo/PdpSharedRegistrationPromo.tsx'
        )
);

// styles
import styles from './PdpSharedCondensedSidebarButtons.scss';

// helpers
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import classnames from 'classnames';
import { trackDriveSavedSearchAdoptionTest } from '../../utils/abTestHelper.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSaveSearchSuccessToast } from '../../shared/SharedFollowTopButton/useSaveSearchSuccessToast.tsx';
import { authLabels, locations } from '../../utils/tracking/shared/followSearchTracking.ts';

const trackSeeFullDetails = () =>
    trackEvent({
        category: 'product',
        action: 'see full details clicked',
        label: 'landing pdp',
        eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_VIEW_MORE_DETAILS,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_VIEW_MORE_DETAILS_CLICK,
        trigger: 'pdp',
    });

const handleSeeFullDetails = (pdpUrl, selectedSkuId) => {
    trackSeeFullDetails();
    let modifiedPdpUrl = pdpUrl;
    if (selectedSkuId) {
        modifiedPdpUrl = `${pdpUrl}#skuId=${selectedSkuId}`;
    }
    window.location.assign(handleLocaleUrl(modifiedPdpUrl, GLOBAL_CLIENT_ONLY_LOCALE));
};

const DEFAULT_QUANTITY = '1';

const PdpSharedCondensedSidebarButtonsComponent = ({
    itemSearch,
    relayVariables,
    user,
    item,
    viewer,
    displayInfo,
    selectedSkuId,
    isClient,
}) => {
    const { itemId, userId } = relayVariables;
    const showFollowCategory = !!displayInfo?.ctas?.showFollowCategory;
    const { isDriveSavedSearchAdoptionVariant } = useServerVarsContext();
    const { showSaveSearchSuccessToast, showRemoveSaveSearchToast } = useSaveSearchSuccessToast();

    useEffect(() => {
        if (showFollowCategory) {
            trackDriveSavedSearchAdoptionTest();
        }
    }, [showFollowCategory]);

    const handleFollowSearchCompleted = useCallback(
        pageIsFollowed => {
            if (!isDriveSavedSearchAdoptionVariant) {
                return;
            }
            if (pageIsFollowed) {
                showSaveSearchSuccessToast();
            } else {
                showRemoveSaveSearchToast();
            }
        },
        [isDriveSavedSearchAdoptionVariant, showRemoveSaveSearchToast, showSaveSearchSuccessToast]
    );

    if (!displayInfo || !displayInfo.ctas) {
        return null;
    }

    const { saveSearchData, pdpURL } = item || {};
    const { showRequestPrice, showMakeOffer, showPurchase, showAcceptOffer } = displayInfo.ctas;
    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    const inlineClassnames = classnames(styles.inlineButtons, {
        [styles.halfWidth]: showPurchase && showMakeOffer,
    });

    return (
        <div className={styles.buttons} data-tn="item-actions">
            {!showFollowCategory && (
                <Button
                    type="primary"
                    className={styles.seeFullDetails}
                    dataTn="item-actions-view-details"
                    onClick={() => handleSeeFullDetails(pdpURL, selectedSkuId)}
                    size="medium"
                >
                    <FormattedMessage
                        id="PdpShareCondensedSidebarButtons.seeFullDetailsCta"
                        defaultMessage="View More Details"
                    />
                </Button>
            )}
            <div className={inlineClassnames}>
                {/* Purchase */}
                {!!showPurchase && (
                    <div className={styles.purchaseButton}>
                        <PdpSharedPurchaseButton
                            viewer={viewer}
                            isPrimary={false}
                            itemId={itemId}
                            item={item}
                            userId={userId}
                            quantitySelected={DEFAULT_QUANTITY}
                            size="medium"
                            skuId={selectedSkuId}
                            hasPrivateOffer={showAcceptOffer}
                        />
                    </div>
                )}
                {/* Make Offer */}
                {!!showMakeOffer && (
                    <CurrencyProvider
                        render={({ currency }) => (
                            <div className={styles.makeOfferButton}>
                                <PdpSharedMakeOfferButton
                                    itemId={itemId}
                                    item={item}
                                    userId={userId}
                                    user={user}
                                    quantitySelected={DEFAULT_QUANTITY}
                                    currency={currency}
                                    showArrow={false}
                                    size="medium"
                                    gaLabel="side rail cta"
                                    hasPrivateOffer={showAcceptOffer}
                                />
                            </div>
                        )}
                    />
                )}
            </div>
            {/* Request Price */}
            {!!showRequestPrice && (
                <PdpSharedContactDealerButton
                    type={'REQUEST_PRICE'}
                    item={item}
                    user={user}
                    buttonType="secondary"
                    size="medium"
                />
            )}
            {showFollowCategory && (
                <SharedSaveSearchContextProvider
                    itemSearch={itemSearch}
                    user={user}
                    searchTerm={followSearchName}
                    uriRef={followSearchPage}
                >
                    <p className={styles.followSearchText}>
                        <FollowSearch
                            searchName={followSearchName}
                            searchNameClassname={styles.searchName}
                        />
                    </p>
                    <SaveSearchButton
                        itemSearch={itemSearch}
                        size="medium"
                        type="primary"
                        locationLabel={authLabels[locations.SOLD_PDP]}
                        onComplete={handleFollowSearchCompleted}
                    />
                </SharedSaveSearchContextProvider>
            )}

            {/* Registration Promo */}
            {isClient && (
                <ClientSuspense fallback={null}>
                    <PdpSharedRegistrationPromo viewer={viewer} paddingTop />
                </ClientSuspense>
            )}
        </div>
    );
};

PdpSharedCondensedSidebarButtonsComponent.propTypes = {
    relayVariables: PropTypes.object.isRequired,
    itemSearch: PropTypes.object,
    viewer: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    userId: PropTypes.string,
    displayInfo: PropTypes.object,
    selectedSkuId: PropTypes.string,
    isClient: PropTypes.bool,
};

export const PdpSharedCondensedSidebarButtons = createFragmentContainer(
    PdpSharedCondensedSidebarButtonsComponent,
    {
        viewer: graphql`
            fragment PdpSharedCondensedSidebarButtons_viewer on Viewer {
                ...PdpSharedRegistrationPromo_viewer @include(if: $isClient)
                ...PdpSharedPurchaseButton_viewer
            }
        `,
        user: graphql`
            fragment PdpSharedCondensedSidebarButtons_user on User {
                ...PdpSharedMakeOfferButton_user
                ...PdpSharedContactDealerButton_user
                ...SharedSaveSearchContext_user
            }
        `,
        item: graphql`
            fragment PdpSharedCondensedSidebarButtons_item on Item {
                ...PdpSharedPurchaseButton_item
                ...PdpSharedContactDealerButton_item
                ...PdpSharedMakeOfferButton_item
                pdpURL
                saveSearchData {
                    name
                    page
                }
            }
        `,
        itemSearch: graphql`
            fragment PdpSharedCondensedSidebarButtons_itemSearch on ItemSearchQueryConnection {
                ...SaveSearchButton_itemSearch
                ...SharedSaveSearchContext_itemSearch
            }
        `,
        displayInfo: graphql`
            fragment PdpSharedCondensedSidebarButtons_displayInfo on ItemDisplayInfo {
                ctas {
                    showAcceptOffer
                    showPurchase
                    showMakeOffer
                    showFollowCategory
                    showRequestPrice
                }
            }
        `,
    }
);
